import { HttpErrors } from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import http from '@/utils/http.service';

const moveAgendaSlotResponseSchema = z.object({
  message: z.string(),
  sourceSlot: z.object({
    id: z.string().uuid(),
    status: z.enum(['open', 'pending', 'confirmed']),
  }),
  targetSlot: z.object({
    id: z.string().uuid(),
    status: z.enum(['open', 'pending', 'confirmed']),
  }),
});

export type MoveAgendaSlotResponse = z.infer<
  typeof moveAgendaSlotResponseSchema
>;

export type MoveAgendaSlotPayload = {
  sourceSlotId: string;
  targetSlotId: string;
  reservationId?: string;
};

const moveAgendaSlot = async (
  payload: MoveAgendaSlotPayload,
): Promise<MoveAgendaSlotResponse> => {
  return http.agendaSlots
    .moveSlot(payload.sourceSlotId, {
      targetSlotId: payload.targetSlotId,
      reservationId: payload.reservationId,
    })
    .then((response) => {
      return moveAgendaSlotResponseSchema.parse(response.data);
    });
};

const useMoveAgendaSlotMutation = (): UseMutationResult<
  MoveAgendaSlotResponse,
  HttpErrors,
  MoveAgendaSlotPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveAgendaSlot,
    onSuccess() {
      toast.success(t('http.move-agenda-slot.success'));
      queryClient.invalidateQueries({ queryKey: ['office-agenda-slots'] });
    },
    onError() {
      toast.error(t('http.move-agenda-slot.error'));
    },
  });
};

export default useMoveAgendaSlotMutation;
